import type { CookieCategoryDefinition, CookieConsentCategory, CookieConsentState } from '#shared/types/cookie-consent'
import { computed, watch } from 'vue'

const CONSENT_COOKIE_KEY = 'cookie-consent'
const CONSENT_VERSION = '2024-11-14'
const CONSENT_MAX_AGE = 60 * 60 * 24 * 180

const categoryDefinitions: CookieCategoryDefinition[] = [
  {
    key: 'necessary',
    label: 'Noodzakelijk',
    description: 'Verplicht voor de werking van de site.',
    required: true,
  },
  {
    key: 'preferences',
    label: 'Voorkeuren',
    description: 'Onthoudt je voorkeuren.',
    required: false,
  },
  {
    key: 'statistics',
    label: 'Statistieken',
    description: 'Helpt ons de site te verbeteren.',
    required: false,
  },
  {
    key: 'marketing',
    label: 'Marketing',
    description: 'Voor personalisatie en campagnes.',
    required: false,
  },
]

const createDefaultState = (): CookieConsentState => ({
  version: CONSENT_VERSION,
  hasInteracted: false,
  categories: categoryDefinitions.reduce((acc, category) => ({
    ...acc,
    [category.key]: category.required,
  }), {} as CookieConsentState['categories']),
})

const sanitizeState = (stored?: CookieConsentState | null): CookieConsentState => {
  const fallback = createDefaultState()

  if (!stored || stored.version !== CONSENT_VERSION)
    return fallback

  const categories: CookieConsentState['categories'] = { ...fallback.categories }

  for (const definition of categoryDefinitions)
    categories[definition.key] = definition.required ? true : Boolean(stored.categories?.[definition.key])

  return {
    version: CONSENT_VERSION,
    hasInteracted: Boolean(stored.hasInteracted),
    consentedAt: stored.consentedAt,
    categories,
  }
}

const buildSelectionMap = (selected: CookieConsentCategory[]): CookieConsentState['categories'] => {
  const selection = new Set<CookieConsentCategory>(selected)

  return categoryDefinitions.reduce((acc, category) => ({
    ...acc,
    [category.key]: category.required ? true : selection.has(category.key),
  }), {} as CookieConsentState['categories'])
}

const applyInteractionMetadata = (state: CookieConsentState): CookieConsentState => ({
  ...state,
  hasInteracted: true,
  consentedAt: new Date().toISOString(),
})

export type CookieConsentComposable = ReturnType<typeof useCookieConsent>
export const cookieCategoryDefinitions = categoryDefinitions

export function useCookieConsent() {
  const consentCookie = useCookie<CookieConsentState | null>(CONSENT_COOKIE_KEY, {
    sameSite: 'lax',
    secure: !import.meta.dev,
    default: () => null,
    maxAge: CONSENT_MAX_AGE,
  })

  const consentState = useState<CookieConsentState>('cookie-consent', () => sanitizeState(consentCookie.value))

  if (import.meta.client) {
    watch(consentState, value => {
      consentCookie.value = value
    }, { deep: true })
  }

  const isBannerVisible = computed(() => !consentState.value.hasInteracted)
  const isSettingsOpen = useState<boolean>('cookie-consent-settings-open', () => false)

  const updateConsent = (categories: CookieConsentState['categories']) => {
    consentState.value = applyInteractionMetadata({
      ...consentState.value,
      categories,
    })
  }

  const acceptAll = () => {
    const categories = buildSelectionMap(categoryDefinitions.map(category => category.key))

    updateConsent(categories)
    isSettingsOpen.value = false
  }

  const rejectNonEssential = () => {
    const categories = buildSelectionMap(categoryDefinitions.filter(category => category.required).map(category => category.key))

    updateConsent(categories)
    isSettingsOpen.value = false
  }

  const saveSelected = (selected: CookieConsentCategory[]) => {
    const categories = buildSelectionMap(selected)

    updateConsent(categories)
    isSettingsOpen.value = false
  }

  const openSettings = () => {
    isSettingsOpen.value = true
  }

  const closeSettings = () => {
    isSettingsOpen.value = false
  }

  const isCategoryEnabled = (category: CookieConsentCategory) => consentState.value.categories[category]

  const resetConsent = () => {
    consentCookie.value = null
    consentState.value = createDefaultState()
    isSettingsOpen.value = true
  }

  return {
    consentState,
    categoryDefinitions,
    isBannerVisible,
    isSettingsOpen,
    acceptAll,
    rejectNonEssential,
    saveSelected,
    openSettings,
    closeSettings,
    isCategoryEnabled,
    resetConsent,
  }
}
